import { z } from 'zod';

import {
  PROXY_TRACE_STEP,
  sanitizeSearch,
  toTraceUrlParams,
  TRACE_PREFIX,
} from '@/services/special-campaign/trace';

// Shared with `proxy.ts` (see `services/special-campaign/trace.ts`).
export { sanitizeSearch, toTraceUrlParams, TRACE_PREFIX };

/**
 * MTRS-2467 investigation: one trace per page load, logged on the server
 * (`logger.service` only reaches Datadog from the server). Steps carry a fixed
 * number so a flow reads in order and a missing number shows where it stopped.
 */
export const TRACE_STEP = {
  proxy: PROXY_TRACE_STEP,
  pageLoad: { no: 1, name: '01_page_load' },
  lineRedirect: { no: 2, name: '02_line_redirect' },
  trackerLookup: { no: 3, name: '03_tracker_lookup' },
  shareModelResolved: { no: 4, name: '04_share_model_resolved' },
  formPrefill: { no: 5, name: '05_form_prefill' },
  eligibility: { no: 6, name: '06_eligibility' },
  confirm: { no: 7, name: '07_confirm' },
  confirmSkipped: { no: 7, name: '07_confirm_skipped' },
  claim: { no: 8, name: '08_claim' },
  lead: { no: 9, name: '09_lead' },
  shareClick: { no: 10, name: '10_share_click' },
  errorPopup: { no: 11, name: '11_error_popup' },
} as const;

export type TraceStepKey = keyof typeof TRACE_STEP;

export type TraceOutcome = 'ok' | 'fail' | 'info';

const TraceValueSchema = z.union([
  z.string().max(500),
  z.number(),
  z.boolean(),
  z.null(),
]);

export type TraceValue = z.infer<typeof TraceValueSchema>;
export type TraceFields = Record<string, TraceValue | undefined>;

export const TraceSchema = z.object({
  traceId: z.string().max(64),
  flow: z.enum(['sharer', 'claimer']),
  seq: z.number().int(),
  stepNo: z.number().int(),
  step: z.string().max(40),
  fields: z.record(z.string().max(40), TraceValueSchema.optional()).optional(),
});

export type TraceContext = z.infer<typeof TraceSchema>;

// Per page load (browser module state).
let traceId: string | undefined;
let seq = 0;

const getTraceId = () => {
  if (!traceId) {
    traceId =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }
  return traceId;
};

/** Browser only: the trace for `step`, numbered in call order. */
export const buildTrace = (
  step: TraceStepKey,
  fields: TraceFields = {}
): TraceContext => {
  const search = typeof window === 'undefined' ? '' : window.location.search;
  const { no, name } = TRACE_STEP[step];

  return {
    traceId: getTraceId(),
    flow: new URLSearchParams(search).has('refTrackerId')
      ? 'claimer'
      : 'sharer',
    seq: ++seq,
    stepNo: no,
    step: name,
    fields,
  };
};
